import React, { useRef, useState, useEffect, useCallback } from 'react'
import Slider, { Settings } from 'react-slick'
import cx from 'classnames'
import Link from 'next/link'
import { CAROUSEL_CARD_WIDTHS, CAROUSEL_CONFIG, CAROUSEL_ANIMATION } from '~constants'
import { useResponsiveSlidesToShow } from 'utils/carousel/useResponsiveSlidesToShow'
import { useWindowSize } from 'utils/hooks'
import breakpoints from 'styles/breakpoints.json'
import styles from './CarouselV2.module.scss'
import ChevronCircleRight from '/public/assets/svg/chevron_circle_right.svg'
import ChevronCircleLeft from '/public/assets/svg/chevron_circle_left.svg'
import { HeadlineType } from '~types'
import { Heading } from '~elements'

type BreakpointKey = keyof typeof breakpoints

export interface CarouselV2Props<T> {
  slides: T[]
  renderSlide: (slide: T, index: number, isChildOfHeroCarousel?: boolean) => React.ReactNode
  className?: string
  allShowsUrl?: string
  enableGlobalKeyboard?: boolean
  peekNextSlide?: boolean
  showAllShowsButton?: boolean
  isChildOfHeroCarousel?: boolean
  carouselSettings?: Settings
  showArrowsBreakpointUp?: BreakpointKey | null
  headline?: HeadlineType
}

const CarouselV2 = <T,>({
  slides,
  className,
  carouselSettings,
  allShowsUrl,
  peekNextSlide = false,
  showArrowsBreakpointUp = 'sm',
  renderSlide,
  enableGlobalKeyboard = true,
  showAllShowsButton = true,
  isChildOfHeroCarousel = false,
  headline
}: CarouselV2Props<T>) => {
  const sliderRef = useRef<Slider>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isAnimatingIn, setIsAnimatingIn] = useState(false)
  const [hasAnimated, setHasAnimated] = useState(false)
  // Use dynamic slides to scroll for the awkward midpoints between
  // our target screen sizes
  const [slidesToScroll, setSlidesToScroll] = useState<number>(CAROUSEL_CONFIG.DEFAULT_SLIDES_TO_SCROLL)
  const [visibleSlides, setVisibleSlides] = useState<number>(CAROUSEL_CONFIG.DEFAULT_SLIDES_TO_SCROLL)
  const slideCount = slides?.length ?? 0
  const currentCardSlidesShown = !isChildOfHeroCarousel
    ? useResponsiveSlidesToShow(carouselSettings?.slidesToShow ?? 1, carouselSettings?.responsive ?? [])
    : null
  const cardCarouselHasMoreSlides = currentCardSlidesShown && slideCount > currentCardSlidesShown
  const { isBreakpoint: isBelowMd } = useWindowSize('md', '<')
  const showArrowsAtBreakpoint =
    showArrowsBreakpointUp == null ? true : useWindowSize(showArrowsBreakpointUp, '>').isBreakpoint
  const shouldPeekNextSlide = peekNextSlide && isBelowMd && cardCarouselHasMoreSlides

  // React slick settings
  const settings: Settings = {
    speed: CAROUSEL_ANIMATION.SPEED,
    slidesToScroll: slidesToScroll,
    slidesToShow: visibleSlides,
    arrows: false,
    autoplay: false,
    dots: false,
    infinite: false,
    beforeChange: (_oldIndex: number, newIndex: number) => setCurrentSlide(newIndex),
    ...carouselSettings
  }

  // Calculate how many slides can be visible based on viewport size
  const calculateVisibleSlides = useCallback(() => {
    if (currentCardSlidesShown) return currentCardSlidesShown

    if (!containerRef.current) return CAROUSEL_CONFIG.DEFAULT_VISIBLE_CARDS
    const containerWidth = containerRef.current.offsetWidth
    const minWidth = CAROUSEL_CARD_WIDTHS.MOBILE * 2 + CAROUSEL_CONFIG.CARD_MARGIN * 2

    if (containerWidth < minWidth) {
      return CAROUSEL_CONFIG.MIN_VISIBLE_CARDS
    }

    let cardWithMargin: number
    const viewportWidth = window.innerWidth
    // We have different card sizes for different breakpoints
    // So we calculate the "visible" slides based off the width
    // of the cards

    if (viewportWidth >= Number(breakpoints.md)) {
      // Card transitions at 1128 to desktop
      cardWithMargin = CAROUSEL_CARD_WIDTHS.DESKTOP + CAROUSEL_CONFIG.CARD_MARGIN_MD_UP
    } else if (viewportWidth >= Number(breakpoints.sm)) {
      // Card transitions at 768 to tablet
      cardWithMargin = CAROUSEL_CARD_WIDTHS.TABLET + CAROUSEL_CONFIG.CARD_MARGIN
    } else {
      cardWithMargin = CAROUSEL_CARD_WIDTHS.MOBILE + CAROUSEL_CONFIG.CARD_MARGIN
    }

    const availableWidth = containerWidth
    const visible = availableWidth / cardWithMargin

    return visible
  }, [])

  // Calculate how many slides to scroll based on visible slides
  const calculateSlidesToScroll = useCallback(() => {
    if (currentCardSlidesShown) return currentCardSlidesShown

    const visible = calculateVisibleSlides()
    const scroll = visible >= CAROUSEL_CONFIG.SCROLL_THRESHOLD ? CAROUSEL_CONFIG.DEFAULT_SLIDES_TO_SCROLL : 1
    return scroll
  }, [calculateVisibleSlides])

  // Debounced resize handler
  // To avoid performance issues on rapid resize events
  useEffect(() => {
    const updateSlideCalculations = () => {
      const newVisibleSlides = calculateVisibleSlides()
      const newSlidesToScroll = calculateSlidesToScroll()
      setVisibleSlides(currentCardSlidesShown || newVisibleSlides)
      setSlidesToScroll(currentCardSlidesShown || newSlidesToScroll)
    }

    // Initial calculation (after layout)
    updateSlideCalculations()

    // Fallback: recalculate after a short timeout in case layout wasn't ready
    const fallbackTimeout = setTimeout(updateSlideCalculations, 100)

    // Debounced resize listener with proper cleanup to prevent race conditions
    let timeoutId: NodeJS.Timeout
    const handleResize = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(updateSlideCalculations, CAROUSEL_CONFIG.RESIZE_DEBOUNCE_MS)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(timeoutId)
      clearTimeout(fallbackTimeout)
    }
  }, [calculateSlidesToScroll, calculateVisibleSlides, visibleSlides, currentCardSlidesShown])

  // Handle initial animation on mount
  useEffect(() => {
    if (!hasAnimated) {
      // Small delay to ensure the component is fully mounted
      const timer = setTimeout(() => {
        setIsAnimatingIn(true)
        setHasAnimated(true)
      }, CAROUSEL_ANIMATION.ENTRANCE_DELAY)

      return () => clearTimeout(timer)
    }
  }, [hasAnimated])

  const goToNext = useCallback(() => {
    if (sliderRef.current) {
      sliderRef.current.slickNext()
    }
  }, [])

  const goToPrev = useCallback(() => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev()
    }
  }, [])

  // Calculate if buttons should be disabled
  const isAtStart = currentSlide === 0

  // Simple end detection based on current slide and slides to scroll
  const isAtEnd = currentSlide + visibleSlides >= slideCount

  // Show arrows only if there are more slides than can fit on screen and prop is true
  const shouldShowArrows = showArrowsAtBreakpoint && slideCount > visibleSlides

  // Global keyboard navigation - works even when carousel isn't focused
  // Can be disabled via props to prevent conflicts with multiple carousels
  useEffect(() => {
    if (!enableGlobalKeyboard) return

    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      // Don't interfere if user is typing in form inputs
      const activeElement = document.activeElement
      if (
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.tagName === 'SELECT' ||
        (activeElement as HTMLElement)?.isContentEditable
      ) {
        return
      }

      if (event.key === 'ArrowLeft' && !isAtStart) {
        event.preventDefault()
        goToPrev()
      } else if (event.key === 'ArrowRight' && !isAtEnd) {
        event.preventDefault()
        goToNext()
      }
    }

    document.addEventListener('keydown', handleGlobalKeyDown)
    return () => document.removeEventListener('keydown', handleGlobalKeyDown)
  }, [isAtStart, isAtEnd, goToPrev, goToNext, enableGlobalKeyboard])

  // Fallback for no slides
  if (!slides || !Array.isArray(slides) || slideCount === 0) {
    return (
      <div className={cx(styles.carouselV2, className)} role='region' aria-label='No events available'>
        <p className={styles.noEventsMessage}>No events to display at this time.</p>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={cx(
        styles.carouselV2,
        className,
        isChildOfHeroCarousel && styles['isChildOfHeroCarousel'],
        // only applies if this is not a child of HeroCarousel
        shouldPeekNextSlide && styles['shouldPeekNextSlide']
      )}
      aria-label='Event carousel'
      role='region'
    >
      <div
        className={cx(styles.topNavigation, {
          [styles.hasButton]: showAllShowsButton && allShowsUrl,
          [styles.hasHeadline]: Boolean(headline)
        })}
      >
        {showAllShowsButton && allShowsUrl && (
          <Link href={allShowsUrl} className={styles.buttonWrapper} aria-label='View all shows' tabIndex={0}>
            All shows
            <svg width='7' height='12' viewBox='0 0 7 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M5.37043 6.01086L0 0.618685L0.615462 0L6.60458 6.01086L0.615462 12L0 11.3813L5.37043 6.01086Z'
                fill='#E2E2E2'
              />
            </svg>
          </Link>
        )}
        {headline && (
          <Heading
            level={headline.level}
            justify={shouldPeekNextSlide ? 'left' : headline.justify}
            levelDisplay={headline.levelDisplay}
            noMarginBottom
            noMarginTop
          >
            {headline.title}
          </Heading>
        )}
        {shouldShowArrows && (
          <div className={styles.arrows}>
            <button
              className={styles.arrow}
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                goToPrev()
              }}
              type='button'
              aria-label={`Previous slide. Currently showing slide ${currentSlide + 1} of ${slideCount}`}
              disabled={isAtStart}
              data-testid='prev-slide-btn'
            >
              <ChevronCircleLeft className={styles.arrowSVG} aria-hidden='true' />
            </button>
            <button
              className={styles.arrow}
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                goToNext()
              }}
              type='button'
              aria-label={`Next slide. Currently showing slide ${currentSlide + 1} of ${slideCount}`}
              disabled={isAtEnd}
              data-testid='next-slide-btn'
            >
              <ChevronCircleRight className={styles.arrowSVG} aria-hidden='true' />
            </button>
          </div>
        )}
      </div>
      <div
        className={cx(styles.sliderContainer, !hasAnimated && styles.initialLoad, isAnimatingIn && styles.animateIn)}
      >
        <Slider ref={sliderRef} {...settings}>
          {slides.map((slide, index) => {
            return renderSlide(slide, index, isChildOfHeroCarousel)
          })}
        </Slider>
      </div>
    </div>
  )
}

export default CarouselV2
